import React from 'react'
import {Satellite, MapPin, Camera, CheckCircle} from 'lucide-react'

export default function VirtualEstimatePage() {
  const howItWorks = [
    {
      icon: MapPin,
      title: 'Enter Your Address',
      description: 'Provide your property address for satellite roof measurement analysis',
    },
    {
      icon: Satellite,
      title: 'Satellite Mapping',
      description: 'High-resolution aerial imagery captures precise roof dimensions and pitch',
    },
    {
      icon: Camera,
      title: 'AI Analysis',
      description: 'Advanced software measures square footage, identifies materials, and detects issues',
    },
    {
      icon: CheckCircle,
      title: 'Receive Estimate',
      description: 'Get detailed roof estimate by address within 24 hours with material recommendations',
    },
  ]

  const benefits = [
    'No need to schedule in-person inspection',
    'Accurate measurements from aerial imagery',
    'Faster turnaround than traditional estimates',
    'Detailed roof condition assessment',
    'Pitch and slope calculations included',
    'Perfect for initial budget planning',
  ]

  return (
    <div className="bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-800 via-blue-900 to-slate-900 text-white py-12 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <Satellite className="text-orange-500 mx-auto mb-4" size={56} />
            <h1 className="text-4xl sm:text-5xl font-bold mb-4">
              Get a Virtual Roof Estimate by Address
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Receive accurate roof estimates near me without leaving your home. Our satellite-powered technology delivers free roof estimate reports using your property address.
            </p>
          </div>
        </div>
      </section>

      {/* How Virtual Estimates Work */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 text-center mb-12">
            How Virtual Roof Estimates Work
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorks.map((step, index) => {
              const Icon = step.icon
              return (
                <div key={index} className="text-center">
                  <div className="bg-blue-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                    <Icon className="text-blue-800" size={36} />
                  </div>
                  <h3 className="font-bold text-xl text-gray-900 mb-3">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Benefits of Virtual Estimates */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-8">
            Benefits of Virtual Roof Estimate Technology
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start bg-white p-6 rounded-lg shadow-md">
                <CheckCircle className="text-orange-500 mr-3 flex-shrink-0 mt-1" size={24} />
                <p className="text-gray-700 text-lg">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 text-center mb-12">
            What's Included in Your Free Roof Estimate
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-blue-50 p-8 rounded-xl text-center">
              <h3 className="font-bold text-2xl text-gray-900 mb-4">Measurements</h3>
              <ul className="text-left text-gray-700 space-y-2">
                <li>• Total roof square footage</li>
                <li>• Roof pitch and slope angles</li>
                <li>• Number of roof planes</li>
                <li>• Ridge and valley lengths</li>
              </ul>
            </div>

            <div className="bg-orange-50 p-8 rounded-xl text-center">
              <h3 className="font-bold text-2xl text-gray-900 mb-4">Cost Breakdown</h3>
              <ul className="text-left text-gray-700 space-y-2">
                <li>• Material costs by type</li>
                <li>• Labor cost estimates</li>
                <li>• Tear off and disposal fees</li>
                <li>• Total project estimate range</li>
              </ul>
            </div>

            <div className="bg-green-50 p-8 rounded-xl text-center">
              <h3 className="font-bold text-2xl text-gray-900 mb-4">Recommendations</h3>
              <ul className="text-left text-gray-700 space-y-2">
                <li>• Best material options</li>
                <li>• Potential problem areas</li>
                <li>• Maintenance suggestions</li>
                <li>• Energy efficiency tips</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Virtual vs. Traditional Roof Estimates Near Me
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg border-2 border-orange-500">
              <h3 className="font-bold text-2xl text-gray-900 mb-4 text-center">Virtual Estimate</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <CheckCircle className="text-green-500 mr-2 flex-shrink-0 mt-1" size={20} />
                  <span>Receive estimate in 24 hours</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-500 mr-2 flex-shrink-0 mt-1" size={20} />
                  <span>No appointment scheduling needed</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-500 mr-2 flex-shrink-0 mt-1" size={20} />
                  <span>Precise satellite measurements</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-500 mr-2 flex-shrink-0 mt-1" size={20} />
                  <span>Free, no-obligation quote</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg border-2 border-gray-300">
              <h3 className="font-bold text-2xl text-gray-900 mb-4 text-center">Traditional Estimate</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-gray-400 mr-2 flex-shrink-0 mt-1">•</span>
                  <span>Wait 3-7 days for appointment</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gray-400 mr-2 flex-shrink-0 mt-1">•</span>
                  <span>Must be home for inspection</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gray-400 mr-2 flex-shrink-0 mt-1">•</span>
                  <span>Manual ladder measurements</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gray-400 mr-2 flex-shrink-0 mt-1">•</span>
                  <span>Follow-up sales calls</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

    </div>
  )
}
